import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import FaceRetouchingOffIcon from '@mui/icons-material/FaceRetouchingOff';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useDashboardService } from '../../services/dashboardService';
import { useSpring, animated } from 'react-spring';

 
const StatItem = ({ icon: Icon, label, value, color = "primary", dense = false }) => {
  return (
    <Box
      sx={{
        p: dense ? 0.5 : 1.5,
        px: dense ? 1 : 2,
        borderRadius: 2,
        boxShadow: 1,
        bgcolor: "background.paper",
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: dense ? 50 : 105,
      }}
    >
      <Icon color={color} sx={{ fontSize: dense ? 20 : 28 }} />
      <Box>
        <Typography variant={dense ? "body2" : "body1"}>{label}</Typography>
        <Typography variant={dense ? "subtitle2" : "h6"} fontWeight="bold">
         {typeof value === 'number' ? value : 0}
        </Typography>
      </Box>
    </Box>
  );
};



const BusinessVerticalStatsWidget = ({ selectedVertical = 'All', selectedDate }) => {
  const { fetchSummaryCounts } = useDashboardService();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      const res = await fetchSummaryCounts({ asOnDate: selectedDate });
      if (!res || !Array.isArray(res)) return;

      if (selectedVertical === 'All') {
        const agg = {
          EmployeeCount: 0,
          CheckInCount: 0,
          CheckOutCount: 0,
          Approved: 0,
          Rejected: 0,
          YetToUpload: 0,
          AwaitingApproval: 0,
          FaceValidationOffCount: 0,
          LocationAssigned: 0,
        };

        res.forEach((row) => {
          agg.EmployeeCount += row.EmployeeCount || 0;
          agg.CheckInCount += row.CheckInCount || 0;
          agg.CheckOutCount += row.CheckOutCount || 0;
          agg.Approved += row.Approved || 0;
          agg.Rejected += row.Rejected || 0;
          agg.YetToUpload += row.YetToUpload || 0;
          agg.AwaitingApproval += row.AwaitingApproval || 0;
          agg.FaceValidationOffCount += row.FaceValidationOffCount || 0;
          agg.LocationAssigned += row.LocationAssigned || 0;
        });

        setStats(agg);
      } else {
        const match = res.find((r) => r.Business_Vertical_Desc === selectedVertical);
        setStats(match || {});
      }
    };

    loadData();
  }, [selectedVertical, selectedDate]);

  if (!stats) return null;

 return (
  <Box>
    <Grid container spacing={2} sx={{ minHeight: 80 }}>
      {/* General Stats */}
      <Grid item size={{xs:6, sm:4, md:3}}>
        <StatItem icon={PeopleIcon} label="Employees" value={stats.EmployeeCount} />
      </Grid>
      <Grid item size={{xs:6, sm:4, md:3}}>
        <StatItem icon={LocationOnIcon} label="Location Assigned" value={stats.LocationAssigned} color="primary" />
      </Grid>
      <Grid item size={{xs:6, sm:4, md:3}}>
        <StatItem icon={LoginIcon} label="Check-Ins" value={stats.CheckInCount} color="success" />
      </Grid>
      <Grid item size={{xs:6, sm:4, md:3}}>
        <StatItem icon={LogoutIcon} label="Check-Outs" value={stats.CheckOutCount} color="info" />
      </Grid>
    </Grid>

    {/* Face Verification Section */}
    <Typography variant="subtitle1" sx={{ mt:2, mb: 1, fontWeight: 'bold' }}>
      Face Verification
    </Typography>

    <Grid container spacing={2} sx={{ minHeight: 80 }} > 
      <Grid item size={{xs:6, sm:3, md:3}}>
        <StatItem icon={UploadFileIcon} label="Yet to Upload" value={stats.YetToUpload} />
      </Grid>
      <Grid item size={{xs:6, sm:3, md:3}}>
        <StatItem icon={HourglassBottomIcon} label="Awaiting Approval" value={stats.AwaitingApproval} color="warning" />
      </Grid>
      <Grid item size={{xs:6, sm:3, md:3}}>
        <StatItem icon={FaceRetouchingOffIcon} label="Face Validation Off" value={stats.FaceValidationOffCount} color="secondary" />
      </Grid>
       <Grid item size={{xs:6, sm:3, md:3}}>
        <Grid container spacing={0.5} direction="column">
            <Grid item>
              <StatItem
                icon={CheckCircleIcon}
                label="Approved"
                value={stats.Approved}
                color="success"
                dense
              />
            </Grid>
            <Grid item>
              <StatItem
                icon={CancelIcon}
                label="Rejected"
                value={stats.Rejected}
                color="error"
                dense
              />
            </Grid>
          </Grid>
        </Grid>

    </Grid>
  </Box>
);

};

export default BusinessVerticalStatsWidget;
